import { useMemo } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import type { AnimationTiming } from 'recharts/types/util/types'
import { ChartCard } from './ChartCard'
import { getChartColorCss } from '../constants/chartColors'
import { CHART_ANIMATION } from '../constants/chartAnimation'
import type { AggregatedChartData } from '../types'

export interface HistogramChartProps {
  readonly data: ReadonlyArray<AggregatedChartData>
  readonly title?: string
  readonly subtitle?: string
  readonly colorOffset?: number
}

/**
 * Histogram Chart - Date cohort visualization
 *
 * Shows distribution of profiles by creation/update date.
 * Useful for understanding user cohorts (vintage vs recent profiles).
 *
 * Features:
 * - Capsule DNA: Rounded bar tops (borderRadius)
 * - Gradient color scale (older → darker, newer → lighter)
 * - Tooltip with date range and count
 * - Animated entry (700ms bounce)
 */
export const HistogramChart: React.FC<HistogramChartProps> = ({
  data,
  title = 'Histogram',
  subtitle,
  colorOffset = 0,
}) => {
  const chartData = useMemo(() => {
    // Sort by label (assuming date format) and limit to reasonable bins
    return [...data]
      .sort((a, b) => {
        const dateA = new Date(a.label).getTime()
        const dateB = new Date(b.label).getTime()
        return dateA - dateB
      })
      .map((item, index) => ({
        name: item.label,
        value: item.value,
        colorIndex: index + colorOffset,
      }))
  }, [data, colorOffset])

  if (chartData.length === 0) {
    return (
      <ChartCard title={title} subtitle={subtitle}>
        <div className="flex items-center justify-center min-h-[160px] text-v11-text-muted text-sm">
          No data available for this period
        </div>
      </ChartCard>
    )
  }

  // Custom bar shape with rounded top (Capsule DNA)
  const RoundedBar = (props: any) => {
    const { fill, x, y, width, height } = props

    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={fill}
          rx={8}
          ry={8}
        />
      </g>
    )
  }

  return (
    <ChartCard title={title} subtitle={subtitle}>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart
          data={chartData}
          margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="currentColor"
            className="text-v11-border opacity-30"
            vertical={false}
          />

          <XAxis
            dataKey="name"
            stroke="currentColor"
            className="text-v11-text-muted text-xs"
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            angle={-45}
            textAnchor="end"
            height={60}
          />

          <YAxis
            stroke="currentColor"
            className="text-v11-text-muted text-xs"
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            width={50}
          />

          <Tooltip
            contentStyle={{
              backgroundColor: 'hsl(var(--v11-surface))',
              border: '1px solid hsl(var(--v11-border))',
              borderRadius: '12px',
              padding: '8px 12px',
            }}
            labelStyle={{
              color: 'hsl(var(--v11-text-primary))',
              fontWeight: 600,
              marginBottom: '4px',
            }}
            itemStyle={{
              color: 'hsl(var(--v11-text-secondary))',
            }}
            formatter={(value: number) => value.toLocaleString()}
            cursor={{ fill: 'hsl(var(--v11-surface-hover))', opacity: 0.5 }}
          />

          <Bar
            dataKey="value"
            shape={<RoundedBar />}
            animationDuration={CHART_ANIMATION.duration}
            animationEasing={CHART_ANIMATION.easing as AnimationTiming}
            isAnimationActive={true}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getChartColorCss(entry.colorIndex)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}
